import { c as _c } from "react/compiler-runtime";
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils";
import SimpleRenderer from "@arcgis/core/renderers/SimpleRenderer";
import { getMarkerSymbol3D, MARKER_3D_LIFT_MIN_TILT_DEG } from "@components/world-map/world-map-constants";
import { useEffect, useState } from "react";

/**
 * Toggles the 3D markers between grounded and lifted based on the camera tilt.
 *
 * Near top-down (tilt below {@link MARKER_3D_LIFT_MIN_TILT_DEG}) the markers rest
 * on their exact coordinate with no callout line; once the camera tilts past the
 * threshold they are raised above the surface with a leader line so buildings
 * don't occlude them. The state only flips when the threshold is crossed, so the
 * data-layer renderer is rebuilt at most once per crossing (not on every tilt
 * change). No-op for the 2D `MapView`.
 *
 * @param viewRef - Ref to the ArcGIS view (only the 3D `SceneView` is handled).
 * @param layerRef - Ref to the data `GeoJSONLayer` whose renderer is updated.
 * @param isViewReady - Whether the view has finished initializing.
 * @param layerVersion - Bumped whenever the data layer is (re)created, so the
 *   current lift state is re-applied to the fresh renderer.
 * @returns Whether the markers are currently lifted, so overlay symbols (the
 *   hover/selected highlight) can match the data markers.
 */
export function useMarker3DLift(viewRef, layerRef, isViewReady, layerVersion) {
  const $ = _c(15);
  const [isLifted, setIsLifted] = useState(false);
  let t0;
  let t1;
  if ($[0] !== isViewReady || $[1] !== viewRef) {
    t0 = () => {
      const view = viewRef.current;
      if (!isViewReady || !view || view.type !== "3d") {
        return;
      }
      const handle = reactiveUtils.watch(() => (view.camera?.tilt ?? 0) >= MARKER_3D_LIFT_MIN_TILT_DEG, isLiftedNow => setIsLifted(isLiftedNow), {
        initial: true
      });
      return () => handle.remove();
    };
    t1 = [viewRef, isViewReady];
    $[0] = isViewReady;
    $[1] = viewRef;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  let t2;
  if ($[4] !== isLifted || $[5] !== isViewReady || $[6] !== layerRef || $[7] !== viewRef) {
    t2 = () => {
      const view_0 = viewRef.current;
      const layer = layerRef.current;
      if (!isViewReady || !layer || view_0?.type !== "3d") {
        return;
      }
      layer.renderer = new SimpleRenderer({
        symbol: getMarkerSymbol3D(isLifted)
      });
    };
    $[4] = isLifted;
    $[5] = isViewReady;
    $[6] = layerRef;
    $[7] = viewRef;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== isLifted || $[10] !== isViewReady || $[11] !== layerRef || $[12] !== layerVersion || $[13] !== viewRef) {
    t3 = [isLifted, isViewReady, layerVersion, viewRef, layerRef];
    $[9] = isLifted;
    $[10] = isViewReady;
    $[11] = layerRef;
    $[12] = layerVersion;
    $[13] = viewRef;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  useEffect(t2, t3);
  return isLifted;
}